'use client';

import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { EffectIntensity } from '@/lib/seasonal-movies';

export function VisualEffects() {
  const { activeTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !activeTheme || !activeTheme.effectType || activeTheme.effectType === 'none') {
    return null;
  }

  const intensity = activeTheme.effectIntensity || 'medium';
  const effectType = activeTheme.effectType;

  // Calculate particle count based on intensity and screen size
  const getParticleCount = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (isMobile) {
      const counts = {
        light: 10,
        medium: 15,
        heavy: 20,
      };
      return counts[intensity];
    }

    const counts = {
      light: 20,
      medium: 40,
      heavy: 60,
    };
    return counts[intensity];
  };

  const particleCount = getParticleCount();

  return (
    <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden" style={{ minHeight: '100vh' }}>
      {effectType === 'snow' && <SnowEffect count={particleCount} />}
      {effectType === 'leaves' && <LeavesEffect count={particleCount} />}
      {effectType === 'bats' && <BatsEffect count={Math.floor(particleCount / 2)} />}
      {effectType === 'fireworks' && <FireworksEffect intensity={intensity} />}
      {effectType === 'hearts' && <HeartsEffect count={particleCount} />}
    </div>
  );
}

// Snow Effect - Improved 3D snowflakes
function SnowEffect({ count }: { count: number }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const maxCount = isMobile ? 30 : 100;

  return (
    <>
      {Array.from({ length: Math.min(count * 2, maxCount) }).map((_, i) => {
        const speed = (Math.random() * 5 + 1) / (Math.random() * 7 + 1) + 10;
        const delay = (Math.random() * 12 + 1) / (Math.random() * 5 + 1);
        const size = Math.random() * 4 + 1;
        const left = Math.random() * 100;
        const opacity = (Math.random() * 7) / 10;

        return (
          <div
            key={i}
            className="snow-particle"
            style={{
              height: `${size * 3}px`,
              left: `${left}vw`,
              opacity: opacity,
              width: `${size}px`,
              animationDuration: `${speed}s, 4s`,
              animationDelay: `${delay}s, ${delay}s`,
            }}
          />
        );
      })}
      <style jsx>{`
        .snow-particle {
          background-color: white;
          border-radius: 30%;
          bottom: 110vh;
          position: absolute;
          animation-name: letItSnow, shakeIt;
          animation-timing-function: linear, linear;
          animation-iteration-count: infinite, infinite;
        }

        .snow-particle:after {
          background-color: white;
          border-radius: 100%;
          content: '';
          height: inherit;
          position: absolute;
          transform: rotate(60deg);
          width: inherit;
        }

        .snow-particle:before {
          background-color: white;
          border-radius: 100%;
          content: '';
          height: inherit;
          position: absolute;
          width: inherit;
          transform: rotate(-60deg);
        }

        @keyframes letItSnow {
          0% {
            bottom: 100vh;
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            bottom: -20vh;
            opacity: 0;
          }
        }

        @keyframes shakeIt {
          0% {
            transform: rotate(-10deg);
          }
          40% {
            transform: rotate(10deg) translateX(0px);
          }
          60% {
            transform: rotate(10deg) translateX(-10px);
          }
          100% {
            transform: rotate(-10deg);
          }
        }
      `}</style>
    </>
  );
}

// Falling Leaves Effect - Multiple leaf images with better animation
function LeavesEffect({ count }: { count: number }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const maxCount = isMobile ? 12 : 24;

  const leafImages = [
    'https://firebasestorage.googleapis.com/v0/b/fire-fotos-8e3f9.appspot.com/o/img%2Fleaves1.png?alt=media&token=3b00e229-fbd0-4b19-b9e0-e841693221d2',
    'https://firebasestorage.googleapis.com/v0/b/fire-fotos-8e3f9.appspot.com/o/img%2Fleaves2.png?alt=media&token=4d391ed7-3d4b-457a-bee2-dae926be7cb3',
    'https://firebasestorage.googleapis.com/v0/b/fire-fotos-8e3f9.appspot.com/o/img%2Fleaves3.png?alt=media&token=25ae3af4-2550-4010-8d10-025fc3c4f1ea',
    'https://firebasestorage.googleapis.com/v0/b/fire-fotos-8e3f9.appspot.com/o/img%2Fleaves4.png?alt=media&token=73cc7170-f391-46f7-8a7e-db7b34708d7f',
  ];

  const positions = [20, 50, 70, 0, 85, 0, 15, 60];
  const durations = [15, 20, 20, 15, 18, 12, 14, 15];
  const delays = [-7, -5, 0, -5, -10, 0, 0, 0];

  return (
    <>
      {Array.from({ length: Math.min(count, maxCount) }).map((_, i) => {
        const leafIndex = i % leafImages.length;
        const posIndex = i % positions.length;
        const durationIndex = i % durations.length;
        const delayIndex = i % delays.length;

        return (
          <div
            key={i}
            className="leaf-particle"
            style={{
              left: `${positions[posIndex]}%`,
              animationDuration: `${durations[durationIndex]}s`,
              animationDelay: `${delays[delayIndex]}s`,
            }}
          >
            <img
              src={leafImages[leafIndex]}
              alt=""
              className="leaf-img"
            />
          </div>
        );
      })}
      <style jsx>{`
        .leaf-particle {
          position: absolute;
          top: -10%;
          z-index: 9999;
          user-select: none;
          cursor: default;
          pointer-events: none;
          display: block;
          opacity: 0;
          animation-name: leaf-animate;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          transform: rotateZ(0) translateX(20px);
        }

        .leaf-img {
          width: 40px;
          height: auto;
          display: block;
        }

        @keyframes leaf-animate {
          10% {
            opacity: 1;
          }
          20% {
            transform: rotateZ(45deg) translateX(-20px);
          }
          40% {
            transform: rotateZ(90deg) translateX(-20px);
          }
          60% {
            transform: rotateZ(180deg) translateX(20px);
          }
          80% {
            transform: rotateZ(180deg) translateX(-20px);
            opacity: 1;
          }
          100% {
            top: 110%;
            transform: rotateZ(225deg) translateX(-20px);
            opacity: 0;
          }
        }

        @media screen and (max-width: 768px) {
          .leaf-img {
            width: 30px;
          }
        }

        @media screen and (max-width: 550px) {
          .leaf-img {
            width: 25px;
          }
        }
      `}</style>
    </>
  );
}

// Flying Bats Effect - Randomly distributed across the screen
function BatsEffect({ count }: { count: number }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const maxCount = isMobile ? 6 : 10;

  // Generate random positions spread across the entire width
  const generateBatData = (totalCount: number) => {
    const bats = [];
    for (let i = 0; i < totalCount; i++) {
      bats.push({
        left: Math.random() * 95 + 2.5, // Random position between 2.5% and 97.5%
        fallDelay: Math.random() * 8, // Random delay between 0-8 seconds
        shakeDelay: Math.random() * 3, // Random shake delay between 0-3 seconds
        duration: 10 + Math.random() * 5, // Random duration between 10-15 seconds
        shakeDuration: 2 + Math.random() * 2, // Random shake duration between 2-4 seconds
        shakeDistance: 40 + Math.random() * 80, // Random shake distance between 40-120px
      });
    }
    return bats;
  };

  const batsData = generateBatData(Math.min(count, maxCount));

  return (
    <>
      {batsData.map((bat, i) => (
        <div
          key={i}
          className="bat-particle"
          style={{
            left: `${bat.left}%`,
            animationDelay: `${bat.fallDelay}s, ${bat.shakeDelay}s`,
            animationDuration: `${bat.duration}s, ${bat.shakeDuration}s`,
            '--shake-distance': `${bat.shakeDistance}px`,
          } as React.CSSProperties}
        >
          <img
            src="https://i.pinimg.com/originals/d1/96/a9/d196a94b9d8cda6edfd3a65237aa8fb5.gif"
            alt=""
            className="bat-img"
          />
        </div>
      ))}
      <style jsx>{`
        .bat-particle {
          position: absolute;
          top: -10%;
          z-index: 9999;
          user-select: none;
          cursor: default;
          animation-name: bats-fall, bats-shake;
          animation-timing-function: linear, ease-in-out;
          animation-iteration-count: infinite, infinite;
          animation-play-state: running, running;
        }

        .bat-img {
          height: 40px;
          width: auto;
          display: block;
        }

        @keyframes bats-fall {
          0% {
            top: -10%;
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            top: 110%;
            opacity: 0;
          }
        }

        @keyframes bats-shake {
          0% {
            transform: translateX(0px);
          }
          25% {
            transform: translateX(calc(var(--shake-distance, 60px) * 0.7));
          }
          50% {
            transform: translateX(var(--shake-distance, 60px));
          }
          75% {
            transform: translateX(calc(var(--shake-distance, 60px) * 0.3));
          }
          100% {
            transform: translateX(0px);
          }
        }
      `}</style>
    </>
  );
}

// Fireworks Effect - Canvas-based realistic fireworks with click interaction
function FireworksEffect({ intensity }: { intensity: EffectIntensity }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize canvas size
    let maxx = window.innerWidth;
    let maxy = window.innerHeight;
    canvas.width = maxx;
    canvas.height = maxy;

    // Handle window resizing
    const handleResize = () => {
      maxx = window.innerWidth;
      maxy = window.innerHeight;
      canvas.width = maxx;
      canvas.height = maxy;
    };
    window.addEventListener('resize', handleResize);

    // Utility functions for randomness
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;
    const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min) + min);
    const randColor = () => `hsl(${randInt(0, 360)}, 100%, 50%)`;

    // Particle class representing individual explosion particles
    class Particle {
      x: number;
      y: number;
      color: string;
      speed: number;
      direction: number;
      vx: number;
      vy: number;
      gravity: number;
      friction: number;
      alpha: number;
      decay: number;
      size: number;

      constructor(x: number, y: number, color: string, speed: number, direction: number, gravity: number, friction: number, size: number) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.speed = speed;
        this.direction = direction;
        this.vx = Math.cos(direction) * speed;
        this.vy = Math.sin(direction) * speed;
        this.gravity = gravity;
        this.friction = friction;
        this.alpha = 1;
        this.decay = rand(0.005, 0.02);
        this.size = size;
      }

      update() {
        this.vx *= this.friction;
        this.vy *= this.friction;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.alpha -= this.decay;
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
        ctx.restore();
      }

      isAlive() {
        return this.alpha > 0;
      }
    }

    // Firework class representing ascending fireworks
    class Firework {
      x: number;
      y: number;
      targetY: number;
      color: string;
      speed: number;
      size: number;
      angle: number;
      vx: number;
      vy: number;
      trail: Array<{ x: number; y: number }>;
      trailLength: number;
      exploded: boolean;

      constructor(x: number, y: number, targetY: number, color: string, speed: number, size: number) {
        this.x = x;
        this.y = y;
        this.targetY = targetY;
        this.color = color;
        this.speed = speed;
        this.size = size;
        this.angle = -Math.PI / 2 + rand(-0.3, 0.3);
        this.vx = Math.cos(this.angle) * this.speed;
        this.vy = Math.sin(this.angle) * this.speed;
        this.trail = [];
        this.trailLength = randInt(10, 25);
        this.exploded = false;
      }

      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > this.trailLength) {
          this.trail.shift();
        }

        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.02;

        if (this.vy >= 0 || this.y <= this.targetY) {
          this.explode();
          return false;
        }
        return true;
      }

      explode() {
        const numParticles = randInt(50, 150);
        for (let i = 0; i < numParticles; i++) {
          const angle = rand(0, Math.PI * 2);
          const speed = rand(2, 7);
          const particleSize = rand(1, 5);
          explosions.push(
            new Particle(
              this.x,
              this.y,
              this.color,
              speed,
              angle,
              0.05,
              0.98,
              particleSize
            )
          );
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.save();
        ctx.beginPath();
        if (this.trail.length > 1) {
          ctx.moveTo(this.trail[0].x, this.trail[0].y);
          for (let point of this.trail) {
            ctx.lineTo(point.x, point.y);
          }
        } else {
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(this.x, this.y);
        }
        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.size;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();
      }
    }

    let fireworks: Firework[] = [];
    let explosions: Particle[] = [];
    let animationId: number;

    // Launch a new firework
    const launchFirework = () => {
      const x = rand(maxx * 0.1, maxx * 0.9);
      const y = maxy;
      const targetY = rand(maxy * 0.1, maxy * 0.4);
      const color = randColor();
      const speed = rand(4, 8);
      const size = rand(2, 5);
      fireworks.push(new Firework(x, y, targetY, color, speed, size));

      // Schedule next firework based on intensity
      const timeout = intensity === 'light' ? rand(800, 1500) : intensity === 'medium' ? rand(500, 1000) : rand(300, 800);
      setTimeout(launchFirework, timeout);
    };

    // Start the first firework launch
    launchFirework();

    // Animation loop
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, maxx, maxy);

      // Update and draw fireworks
      for (let i = fireworks.length - 1; i >= 0; i--) {
        const firework = fireworks[i];
        if (!firework.update()) {
          fireworks.splice(i, 1);
        } else {
          firework.draw(ctx);
        }
      }

      // Update and draw explosion particles
      for (let i = explosions.length - 1; i >= 0; i--) {
        const particle = explosions[i];
        particle.update();
        if (particle.isAlive()) {
          particle.draw(ctx);
        } else {
          explosions.splice(i, 1);
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Click interaction - launch fireworks on click
    const handleClick = (event: MouseEvent) => {
      const x = event.clientX;
      const y = maxy;
      const targetY = event.clientY;
      const color = randColor();
      const speed = rand(4, 8);
      const size = rand(2, 5);
      fireworks.push(new Firework(x, y, targetY, color, speed, size));
    };
    window.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('click', handleClick);
      cancelAnimationFrame(animationId);
    };
  }, [intensity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

// Floating Hearts Effect
function HeartsEffect({ count }: { count: number }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const maxCount = isMobile ? 15 : count;

  return (
    <>
      {Array.from({ length: Math.min(count, maxCount) }).map((_, i) => (
        <div
          key={i}
          className="heart-particle"
          style={{
            left: `${Math.random() * 100}%`,
            animationDuration: `${Math.random() * 4 + 4}s`,
            animationDelay: `${Math.random() * 5}s`,
            fontSize: `${Math.random() * 15 + 15}px`,
            opacity: Math.random() * 0.6 + 0.4,
          }}
        >
          ❤️
        </div>
      ))}
      <style jsx>{`
        .heart-particle {
          position: absolute;
          bottom: -50px;
          animation: heartfloat linear infinite;
        }

        @keyframes heartfloat {
          0% {
            transform: translateY(0) translateX(0) scale(1);
            opacity: 0.6;
          }
          20% {
            opacity: 1;
          }
          50% {
            transform: translateY(-50vh) translateX(${Math.random() * 50 - 25}px) scale(1.2);
            opacity: 1;
          }
          80% {
            opacity: 0.8;
          }
          100% {
            transform: translateY(-100vh) translateX(0) scale(0.8);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
}

